import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Recipe } from '../../models/Recipe';
import { getRecipeThumbnail } from '../../utils/RecipeService';

type Props = {
  recipes: Recipe[];
  currentIndex: number;
  onLike: (recipe: Recipe) => void;
  onPass: () => void;
  onNewSession: () => void;
};

function formatCookTime(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

const RecipeMatchSwipe = ({ recipes, currentIndex, onLike, onPass, onNewSession }: Props) => {
  const { t } = useTranslation();
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [animatingOut, setAnimatingOut] = useState<'left' | 'right' | null>(null);
  const [thumbnails, setThumbnails] = useState<Record<number, string>>({});
  const loadedIds = useRef<Set<number>>(new Set());
  const dragStartX = useRef(0);

  // Preload thumbnails for current card + next 2
  useEffect(() => {
    recipes.slice(currentIndex, currentIndex + 3).forEach(r => {
      if (r.id != null && !loadedIds.current.has(r.id)) {
        loadedIds.current.add(r.id);
        getRecipeThumbnail(r.id).then(blob => {
          if (blob) setThumbnails(prev => ({ ...prev, [r.id!]: URL.createObjectURL(blob) }));
        });
      }
    });
  }, [currentIndex, recipes]);

  const completeSwipe = useCallback((direction: 'left' | 'right') => {
    if (animatingOut) return;
    setIsDragging(false);
    setAnimatingOut(direction);
    const recipe = recipes[currentIndex];
    setTimeout(() => {
      setDragX(0);
      setAnimatingOut(null);
      if (direction === 'right') onLike(recipe);
      else onPass();
    }, 280);
  }, [animatingOut, recipes, currentIndex, onLike, onPass]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (animatingOut) return;
    setIsDragging(true);
    dragStartX.current = e.clientX;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [animatingOut]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    setDragX(e.clientX - dragStartX.current);
  }, [isDragging]);

  const handlePointerUp = useCallback(() => {
    if (!isDragging || animatingOut) return;
    setIsDragging(false);
    if (Math.abs(dragX) > 80) {
      completeSwipe(dragX > 0 ? 'right' : 'left');
    } else {
      setDragX(0);
    }
  }, [isDragging, animatingOut, dragX, completeSwipe]);

  const recipe = recipes[currentIndex];
  if (!recipe) return null;

  const swipeX = animatingOut === 'right' ? 340 : animatingOut === 'left' ? -340 : dragX;
  const rotation = swipeX * 0.08;
  const showYum = swipeX > 40;
  const showPass = swipeX < -40;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, px: 2 }}>
      {/* Progress */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {currentIndex + 1} / {recipes.length}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={((currentIndex + 1) / recipes.length) * 100}
            sx={{ width: 56, borderRadius: 4, height: 5 }}
          />
          <IconButton
            size="small"
            onClick={onNewSession}
            title={t('match_newSession')}
            sx={{ color: 'text.disabled', p: 0.25 }}
          >
            <RestartAltIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Card stack */}
      <Box sx={{ position: 'relative', flex: 1, mx: 0.5 }}>
        {/* Back cards */}
        {[2, 1].map(offset => {
          if (currentIndex + offset >= recipes.length) return null;
          return (
            <Box key={offset} sx={{
              position: 'absolute',
              inset: `${offset * 8}px ${offset * 4}px ${offset * 6}px`,
              border: '2px solid', borderColor: 'divider',
              borderRadius: '20px', bgcolor: 'background.paper',
              transform: `rotate(${offset % 2 === 0 ? 3 : -2}deg)`,
            }} />
          );
        })}

        {/* Active card */}
        <Box
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={() => { setIsDragging(false); setDragX(0); }}
          sx={{
            position: 'absolute', inset: '6px 0 12px',
            border: '2.5px solid', borderColor: 'text.primary',
            borderRadius: '20px', bgcolor: 'background.paper',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none', touchAction: 'none',
            transform: `rotate(${rotation}deg) translateX(${swipeX}px)`,
            transition: isDragging
              ? 'none'
              : animatingOut
                ? 'transform 0.28s ease, opacity 0.28s ease'
                : 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
            opacity: animatingOut ? 0 : 1,
            boxShadow: 6,
          }}
        >
          {/* Photo */}
          <Box sx={{
            flex: 1,
            backgroundImage: recipe.id != null && thumbnails[recipe.id]
              ? `url(${thumbnails[recipe.id]})` : undefined,
            backgroundSize: 'cover', backgroundPosition: 'center',
            bgcolor: recipe.id != null && thumbnails[recipe.id] ? undefined : 'grey.100',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {(recipe.id == null || !thumbnails[recipe.id]) && (
              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                [ recipe photo ]
              </Typography>
            )}
          </Box>

          {/* Info */}
          <Box sx={{ p: '10px 12px 12px', borderTop: '1.5px solid', borderColor: 'divider' }}>
            <Typography fontWeight={700} fontSize="1.1rem">{recipe.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
                <AccessTimeIcon sx={{ fontSize: 13 }} />
                <Typography variant="caption">{formatCookTime(recipe.cookTime)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
                <RestaurantIcon sx={{ fontSize: 13 }} />
                <Typography variant="caption">{recipe.yield}</Typography>
              </Box>
              {recipe.difficulty && (
                <Typography variant="caption" color="text.secondary">{recipe.difficulty}</Typography>
              )}
            </Box>
            {recipe.tags && recipe.tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                {recipe.tags.slice(0, 3).map(tag => (
                  <Box key={tag} sx={{
                    border: '1px solid', borderColor: 'divider',
                    borderRadius: '10px', px: 1, py: 0.25,
                    fontSize: '0.68rem', color: 'text.secondary',
                  }}>
                    {tag}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Stamps */}
          {showYum && (
            <Box sx={{
              position: 'absolute', top: 30, right: 18,
              transform: 'rotate(14deg)',
              border: '3px solid', borderColor: 'primary.main', color: 'primary.main',
              fontWeight: 700, fontSize: '1.55rem', px: 1.5, py: 0.25,
              borderRadius: 2, bgcolor: 'rgba(255,255,255,0.4)', pointerEvents: 'none',
            }}>
              {t('match_yum')}
            </Box>
          )}
          {showPass && (
            <Box sx={{
              position: 'absolute', top: 50, left: 18,
              transform: 'rotate(-14deg)',
              border: '2.5px dashed', borderColor: 'text.disabled', color: 'text.disabled',
              fontWeight: 700, fontSize: '1.1rem', px: 1, py: 0.25,
              borderRadius: 2, pointerEvents: 'none',
            }}>
              {t('match_pass')}
            </Box>
          )}
        </Box>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4.5, pb: 2, pt: 1 }}>
        <IconButton
          onClick={() => completeSwipe('left')}
          disabled={!!animatingOut}
          sx={{
            width: 54, height: 54,
            border: '2.5px solid', borderColor: 'text.primary',
            bgcolor: 'background.paper', color: 'text.secondary',
            '&:hover': { bgcolor: 'background.default' },
          }}
        >
          <CloseIcon />
        </IconButton>
        <IconButton
          onClick={() => completeSwipe('right')}
          disabled={!!animatingOut}
          sx={{
            width: 54, height: 54,
            border: '2.5px solid', borderColor: 'primary.main',
            bgcolor: '#fbe9d6', color: 'primary.main',
            '&:hover': { bgcolor: '#f5d9b8' },
          }}
        >
          <FavoriteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default RecipeMatchSwipe;
